import * as React from 'react';
import { defaultPasskeyContext, PasskeyContext, PasskeyRegistrationState } from '../PasskeyContext';
import { useServiceResolver } from '@services/useServiceResolver';
import { useAppContext } from '@contexts/app/hooks/useAppContext';
import { ResponseCode } from '@ul/api';
import { PasskeyWeb } from '@ul/common';
import { usePushNotification } from '@components/Notification/useNotification';
import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { LoginFormCmsWithDefault } from '@components/forms/login/LoginForm/LoginFormCms';
import { RegisterPasskeyFormCmsWithDefault } from '@v2/components/Forms/RegisterPasskeyForm/RegisterPasskeyFormCmsWithDefault';
import type { RegistrationResponseJSON, AuthenticationResponseJSON } from '@simplewebauthn/browser';
import type {
    PublicKeyCredentialCreationOptionsJSON,
    PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';

import {
    PasskeyRegistrationPlatformResponse,
    PasskeyAuthenticationPlatformResponse,
    PasskeyRegistrationExitState,
} from '@ul/common';
import { useIsMobileApp } from '@contexts/app/hooks/useIsMobileApp';
import { useAppSetting } from '@contexts/appSetting/hooks/useAppSetting';
import { useAnalytics } from './analytics';

const USER_CANCELLED_REGISTRATION = 'User cancelled the registration';
const USER_CANCELLED_AUTH = 'User cancelled the authentication';
const AUTH_ABORTED = 'Authentication was cancelled';

const METRIC_PASSKEY = 'ul.clientside.passkey';

export const METRIC_TYPES = {
    PLATFORM_AUTH_REQUEST: 'platform_authenticate_request',
    PLATFORM_AUTH_RESPONSE: 'platform_authenticate_response',
    UL_AUTH_RESPONSE: 'ul_authenticate_response',
    UL_REGISTER_RESPONSE: 'ul_register_response',
    PLATFORM_REGISTER_REQUEST: 'platform_register_request',
    PLATFORM_REGISTER_RESPONSE: 'platform_register_response',
    PASSKEY_REGISTER_EXIT: 'passkey_registration_exit',
} as const;

export function withPasskeyContext<TProps>(Component: React.FC<TProps>): React.FC<TProps> {
    const ComponentWithPasskeyContext: React.FC<TProps> = props => {
        const { loginService, sdkEffect, measurement, logger } = useServiceResolver();
        const { passkeySupported, iframeMode, appId } = useAppContext();
        const isMobileApp = useIsMobileApp();
        const { feature } = useAppSetting();

        const pushNotification = usePushNotification('RegisterPasskey');
        const pushUnifiedAuthNotification = usePushNotification('UnifiedAuth');
        const legacyFormTranslation = useTranslation(LoginFormCmsWithDefault);
        const translation = useTranslation(RegisterPasskeyFormCmsWithDefault);
        const {
            AnalyticsPasskeyTechnicalErrorNotificationProps,
            AnalyticsPasskeyCreatedNotificationProps,
        } = useAnalytics();

        const [registrationState, setRegistrationState] = React.useState<PasskeyRegistrationState>('idle');
        const [isLoading, setIsLoading] = React.useState(false);
        const [isPasskeyAutofillSupported, setIsPasskeyAutofillSupported] = React.useState(false);
        const [error, setError] = React.useState<string | null>(null);

        // Upstream can enable passkeys for rollout/host support; on web we additionally require actual WebAuthn support.
        const isSupported = !!passkeySupported && (isMobileApp ? true : PasskeyWeb.isSupported());

        const measurementTags = {
            appId: appId,
            origin: origin,
        };

        // Get passkey registration options from UL-API
        const getPasskeyRegistrationOptions = React.useCallback(async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await loginService.getPasskeyRegistrationOptions();

                return response;
            } catch (err) {
                setError('Network error while getting registration options');
                throw err;
            } finally {
                setIsLoading(false);
            }
        }, [loginService]);

        // Request passkey registration on platform (browser WebAuthn)
        const requestRegistrationOnPlatform = React.useCallback(
            async (options: PublicKeyCredentialCreationOptionsJSON): Promise<PasskeyRegistrationPlatformResponse> => {
                try {
                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.PLATFORM_REGISTER_REQUEST,
                    });
                    setIsLoading(true);
                    setRegistrationState('platform_register');

                    // Call SDK effect to communicate with parent/platform
                    const platformResponse = await sdkEffect.onPasskeyRegister(options);

                    return platformResponse;
                } catch (err) {
                    setError('Platform registration failed');
                    setRegistrationState('error');
                    setIsLoading(false);
                    pushNotification({
                        color: 'error',
                        message: legacyFormTranslation.passkeyRegistrationFailed,
                        testId: 'passkey-registration-failed',
                        analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                    });
                    throw err;
                }
            },
            [
                sdkEffect,
                legacyFormTranslation,
                pushNotification,
                AnalyticsPasskeyTechnicalErrorNotificationProps,
                measurement,
                measurementTags,
            ]
        );

        const onPasskeyRegistrationExit = React.useCallback(
            (exitState: PasskeyRegistrationExitState) => {
                measurement.count(METRIC_PASSKEY, 1, {
                    ...measurementTags,
                    type: METRIC_TYPES.PASSKEY_REGISTER_EXIT,
                    state: exitState.toString(),
                });
                sdkEffect.onPasskeyRegistrationExit(exitState);
            },
            [sdkEffect, measurement, measurementTags]
        );

        // Complete registration with UL-API
        const registerPasskey = React.useCallback(
            async (platformResponse: RegistrationResponseJSON, goToVerifyOtpCallback?: () => void) => {
                try {
                    setIsLoading(true);
                    setRegistrationState('completing');

                    const response = await loginService.registerPasskey({
                        responseJson: JSON.stringify(platformResponse),
                    });

                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.UL_REGISTER_RESPONSE,
                        success: (response.responseCode === ResponseCode.Successful).toString(),
                        responseCode: response.responseCode.toString(),
                    });

                    if (response.responseCode === ResponseCode.Successful) {
                        setRegistrationState('success');
                        pushNotification({
                            color: 'success',
                            message: translation.RegisterPasskeySuccess,
                            testId: 'passkey-registration-success',
                            analyticsProps: AnalyticsPasskeyCreatedNotificationProps,
                        });
                        setTimeout(() => {
                            onPasskeyRegistrationExit(PasskeyRegistrationExitState.PASSKEY_CREATED);
                        }, 2000);
                    } else if (response.responseCode === ResponseCode.NeedOtp) {
                        setRegistrationState('need_otp');
                        if (goToVerifyOtpCallback) {
                            goToVerifyOtpCallback();
                        }
                    } else {
                        setError('Failed to complete passkey registration');
                        setRegistrationState('error');
                        pushNotification({
                            color: 'error',
                            message: legacyFormTranslation.passkeyRegistrationFailed,
                            testId: 'passkey-registration-failed',
                            analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                        });
                    }
                } catch (err) {
                    setError('Network error during completion');
                    setRegistrationState('error');
                    pushNotification({
                        color: 'error',
                        message: legacyFormTranslation.passkeyRegistrationFailed,
                        testId: 'passkey-registration-failed',
                        analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                    });
                } finally {
                    setIsLoading(false);
                }
            },
            [
                loginService,
                legacyFormTranslation,
                pushNotification,
                translation,
                onPasskeyRegistrationExit,
                AnalyticsPasskeyTechnicalErrorNotificationProps,
                AnalyticsPasskeyCreatedNotificationProps,
                measurement,
                measurementTags,
            ]
        );

        // Perform full passkey registration flow
        const performPasskeyRegistration = React.useCallback(
            async (goToVerifyOtpCallback?: () => void) => {
                try {
                    setIsLoading(true);
                    setError(null);
                    setRegistrationState('initiating');

                    // Step 1: Get registration options from server
                    const response = await getPasskeyRegistrationOptions();

                    // Check if OTP is needed
                    if (response.responseCode === ResponseCode.NeedOtp) {
                        setRegistrationState('need_otp');
                        // Use callback if provided, otherwise fall back to context's goToVerifyOtp
                        if (goToVerifyOtpCallback) {
                            goToVerifyOtpCallback();
                        }
                        return response;
                    }

                    if (!response.success || response.responseCode !== ResponseCode.Successful) {
                        setError('Failed to initiate passkey registration');
                        setRegistrationState('error');
                        pushNotification({
                            color: 'error',
                            message: legacyFormTranslation.passkeyRegistrationFailed,
                            testId: 'passkey-registration-failed',
                            analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                        });
                        return response;
                    }

                    if (response.success && response.options) {
                        // Step 2: Register on platform (shows native prompt, returns credential)
                        const platformResponse = await requestRegistrationOnPlatform(response.options);

                        const registerTags: Record<string, string> = {
                            ...measurementTags,
                            type: METRIC_TYPES.PLATFORM_REGISTER_RESPONSE,
                            success: platformResponse.success.toString(),
                        };
                        if (!platformResponse.success) {
                            logger.error(
                                'Passkey registration error platform response: ' +
                                    JSON.stringify(platformResponse.error)
                            );
                            let errorTag: string;
                            switch (platformResponse.error) {
                                case USER_CANCELLED_REGISTRATION:
                                    errorTag = 'cancelled';
                                    break;
                                default:
                                    errorTag = 'error';
                            }
                            registerTags.error = errorTag;
                        }
                        measurement.count(METRIC_PASSKEY, 1, registerTags);

                        if (!platformResponse.success || !platformResponse.responseJSON) {
                            if (platformResponse.error === USER_CANCELLED_REGISTRATION) {
                                setRegistrationState('idle');
                                setIsLoading(false);
                                return response;
                            }

                            throw new Error(platformResponse.error || 'Failed to get credential from platform');
                        }

                        // Step 3: Complete registration with server - pass the callback through
                        await registerPasskey(platformResponse.responseJSON, goToVerifyOtpCallback);
                    }

                    return response;
                } catch (err) {
                    logger.error('Passkey registration flow error', err);
                    setError('Network error during registration');
                    setRegistrationState('error');
                    setIsLoading(false);
                    pushNotification({
                        color: 'error',
                        message: legacyFormTranslation.passkeyRegistrationFailed,
                        testId: 'passkey-registration-failed',
                        analyticsProps: AnalyticsPasskeyTechnicalErrorNotificationProps,
                    });
                    throw err;
                }
            },
            [
                getPasskeyRegistrationOptions,
                requestRegistrationOnPlatform,
                registerPasskey,
                legacyFormTranslation,
                pushNotification,
                AnalyticsPasskeyTechnicalErrorNotificationProps,
                measurement,
                measurementTags,
            ]
        );

        // Get passkey authentication options from UL-API
        const getPasskeyAuthenticationOptions = React.useCallback(async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await loginService.getPasskeyAuthenticationOptions();

                return response;
            } catch (err) {
                setError('Network error during authentication initiation');
                throw err;
            } finally {
                setIsLoading(false);
            }
        }, [loginService]);

        // Complete authentication with UL-API
        const authenticateWithPasskey = React.useCallback(
            async (platformResponse: AuthenticationResponseJSON) => {
                try {
                    setIsLoading(true);
                    setError(null);

                    const response = await loginService.authenticateWithPasskey({
                        responseJson: JSON.stringify(platformResponse),
                    });

                    if (response.responseCode !== ResponseCode.Successful) {
                        setError('Authentication failed');
                    }

                    return response;
                } catch (err) {
                    setError('Network error during authentication completion');
                    throw err;
                } finally {
                    setIsLoading(false);
                }
            },
            [loginService]
        );

        // Request authentication on platform (browser/native)
        // Returns the platform response for the component to handle
        const requestAuthenticationOnPlatform = React.useCallback(
            async (
                options: PublicKeyCredentialRequestOptionsJSON,
                useConditionalMediation: boolean = false
            ): Promise<PasskeyAuthenticationPlatformResponse> => {
                try {
                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.PLATFORM_AUTH_REQUEST,
                        conditional: useConditionalMediation ? 'true' : 'false',
                    });
                    let platformResponse;
                    if (iframeMode && !isMobileApp && useConditionalMediation) {
                        platformResponse = await PasskeyWeb.startAuthentication(options, useConditionalMediation);
                    } else {
                        platformResponse = await sdkEffect.onPasskeyAuthenticate(options, useConditionalMediation);
                    }

                    return platformResponse;
                } catch (err) {
                    throw err;
                }
            },
            [sdkEffect, measurement, measurementTags]
        );

        // Reset registration state
        const resetRegistration = React.useCallback(() => {
            setRegistrationState('idle');
            setIsLoading(false);
            setError(null);
        }, []);

        const handleBackButtonClick = React.useCallback(async () => {
            onPasskeyRegistrationExit(PasskeyRegistrationExitState.PASSKEY_BACK);
        }, [onPasskeyRegistrationExit]);

        // Check if conditional UI (autofill) is supported on platform
        const checkAutofillSupportOnPlatform = React.useCallback(async (): Promise<boolean> => {
            try {
                if (isMobileApp) {
                    const isSupported = await sdkEffect.onPasskeySupportsAutofill();
                    return isSupported;
                }
                const isSupported = await PasskeyWeb.supportsAutofill();
                return isSupported;
            } catch (error) {
                return false;
            }
        }, [sdkEffect, isMobileApp]);

        React.useEffect(() => {
            if (feature?.passkeyAuthentication) {
                checkAutofillSupportOnPlatform().then(isSupported => {
                    setIsPasskeyAutofillSupported(isSupported);
                });
            }
        }, [checkAutofillSupportOnPlatform, feature?.passkeyAuthentication]);

        const abortPasskeyRequestOnPlatform = React.useCallback(() => {
            if (iframeMode) {
                PasskeyWeb.abortPasskeyRequest();
            }
        }, [iframeMode]);

        const performPasskeyAuthentication = React.useCallback(
            async (
                onSuccess: (responseCode: ResponseCode) => void,
                useAutofill: boolean = false,
                isInputEmailFlow: boolean = false,
                onCancelCallback: (checkPasskey: boolean) => void
            ) => {
                try {
                    // Step 1: Get authentication options from server
                    const authResponse = await getPasskeyAuthenticationOptions();

                    if (!authResponse.options) {
                        return;
                    }

                    // Step 2: Authenticate on platform (shows native prompt, returns credential)
                    const platformResponse = await requestAuthenticationOnPlatform(authResponse.options, useAutofill);

                    // For autofill, check success before proceeding
                    const authTags: Record<string, string> = {
                        ...measurementTags,
                        type: METRIC_TYPES.PLATFORM_AUTH_RESPONSE,
                        success: platformResponse.success.toString(),
                    };
                    if (!platformResponse.success) {
                        if (isInputEmailFlow) {
                            if (onCancelCallback) onCancelCallback(false);
                        }
                        logger.error(
                            `Passkey authentication error platform response: ${JSON.stringify(platformResponse?.error)}`
                        );
                        let errorTag: string;
                        switch (platformResponse.error) {
                            case USER_CANCELLED_AUTH:
                                errorTag = 'cancelled';
                                break;
                            case AUTH_ABORTED:
                                errorTag = 'aborted';
                                break;
                            default:
                                errorTag = 'error';
                        }
                        authTags.error = errorTag;
                    }
                    measurement.count(METRIC_PASSKEY, 1, authTags);

                    if (!platformResponse.success) {
                        // For background autofill attempts, stay silent to avoid noisy UX.
                        // For explicit user action, show a generic fallback message.
                        // shouldn't show error in case user cancel. passkey prompt from input email flow.
                        if (
                            !useAutofill &&
                            !isInputEmailFlow &&
                            platformResponse.error !== USER_CANCELLED_AUTH &&
                            platformResponse.error !== AUTH_ABORTED
                        ) {
                            pushUnifiedAuthNotification({
                                color: 'error',
                                message: legacyFormTranslation.passkeyAuthenticationFailed,
                                testId: 'passkey-authentication-failed',
                            });
                        }
                        return;
                    }

                    if (!platformResponse.responseJSON) {
                        throw new Error(platformResponse.error || 'Failed to get credential from platform');
                    }

                    // Step 3: Complete authentication with server
                    const completeResponse = await authenticateWithPasskey(platformResponse.responseJSON);

                    measurement.count(METRIC_PASSKEY, 1, {
                        ...measurementTags,
                        type: METRIC_TYPES.UL_AUTH_RESPONSE,
                        success: (completeResponse.responseCode === ResponseCode.Successful).toString(),
                        responseCode: completeResponse.responseCode.toString(),
                    });

                    onSuccess(completeResponse.responseCode);
                } catch (error) {
                    logger.error('Passkey authentication flow error', error);
                    // silently ignore AbortError (user likely clicked elsewhere)
                    if (error?.name === 'AbortError') {
                        return;
                    }

                    // Only rethrow for regular authentication
                    if (!useAutofill) {
                        pushUnifiedAuthNotification({
                            color: 'error',
                            message: legacyFormTranslation.passkeyAuthenticationFailed,
                            testId: 'passkey-authentication-failed',
                        });
                        throw error;
                    }
                }
            },
            [
                getPasskeyAuthenticationOptions,
                requestAuthenticationOnPlatform,
                authenticateWithPasskey,
                legacyFormTranslation,
                pushUnifiedAuthNotification,
                measurement,
                measurementTags,
            ]
        );

        const updateLocalPasskeyPreference = (skipPasskey?: boolean) => {
            localStorage.setItem('passkeyPreference', JSON.stringify({ skipPasskey: !!skipPasskey }));
        };

        const contextValue = React.useMemo(
            () => ({
                registrationState,
                isLoading,
                error,
                isSupported,
                isPasskeyAutofillSupported,
                getPasskeyRegistrationOptions,
                performPasskeyRegistration,
                getPasskeyAuthenticationOptions,
                requestAuthenticationOnPlatform,
                authenticateWithPasskey,
                requestRegistrationOnPlatform,
                registerPasskey,
                resetRegistration,
                setRegistrationState,
                handleBackButtonClick,
                checkAutofillSupportOnPlatform,
                onPasskeyRegistrationExit,
                abortPasskeyRequestOnPlatform,
                performPasskeyAuthentication,
                updateLocalPasskeyPreference,
            }),
            [
                registrationState,
                isLoading,
                error,
                isSupported,
                isPasskeyAutofillSupported,
                getPasskeyRegistrationOptions,
                performPasskeyRegistration,
                getPasskeyAuthenticationOptions,
                requestAuthenticationOnPlatform,
                authenticateWithPasskey,
                requestRegistrationOnPlatform,
                registerPasskey,
                resetRegistration,
                handleBackButtonClick,
                checkAutofillSupportOnPlatform,
                onPasskeyRegistrationExit,
                abortPasskeyRequestOnPlatform,
                performPasskeyAuthentication,
                updateLocalPasskeyPreference,
            ]
        );

        return (
            <PasskeyContext.Provider value={contextValue}>
                <Component {...props} />
            </PasskeyContext.Provider>
        );
    };

    ComponentWithPasskeyContext.displayName = `${Component.displayName || 'Component'}WithPasskeyContext`;

    return ComponentWithPasskeyContext;
}
